import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '@formosa/shared/api/admin';
import { EMPTY_EMOJI_FORM } from './reactionConstants';

export default function ReactionEmojiPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const packId = params.get('packId');
  const [packName, setPackName] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_EMOJI_FORM);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!packId) return;
    setLoading(true);
    setError('');
    try {
      const [pack, list] = await Promise.all([
        adminApi.getImReactionPack(packId),
        adminApi.listImReactionEmojis(packId),
      ]);
      setPackName(pack?.name || '');
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!packId) return;
    setBusy(true);
    setError('');
    try {
      if (editId) {
        await adminApi.updateImReactionEmoji(editId, {
          code: form.code,
          name: form.name,
          keywords: form.keywords,
          status: form.status,
          sort: form.sort,
        });
      } else {
        await adminApi.createImReactionEmoji(packId, form);
      }
      setForm(EMPTY_EMOJI_FORM);
      setEditId(null);
      await load();
    } catch (err) {
      setError(err.message || '儲存失敗');
    } finally {
      setBusy(false);
    }
  }

  if (!packId) {
    return (
      <div className="page-card">
        <p>缺少 packId</p>
        <button type="button" className="btn-link" onClick={() => navigate('/system/sticker-manage/reaction')}>
          返回
        </button>
      </div>
    );
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>表情項目 — {packName || packId}</h2>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/system/sticker-manage/reaction')}
        >
          返回包列表
        </button>
      </div>
      {error ? <p className="form-error">{error}</p> : null}

      <form className="form-grid" onSubmit={handleSubmit}>
        <label>
          Emoji {editId ? '(不可改)' : ''}
          <input
            required={!editId}
            disabled={!!editId}
            value={form.emoji}
            onChange={(e) => setForm((p) => ({ ...p, emoji: e.target.value }))}
          />
        </label>
        <label>
          code
          <input
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
          />
        </label>
        <label>
          名稱
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
        </label>
        <label>
          keywords
          <input
            value={form.keywords}
            onChange={(e) => setForm((p) => ({ ...p, keywords: e.target.value }))}
          />
        </label>
        <label>
          狀態
          <select
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
          >
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
            <option value="BANNED">BANNED</option>
          </select>
        </label>
        <label>
          排序
          <input
            type="number"
            value={form.sort}
            onChange={(e) => setForm((p) => ({ ...p, sort: Number(e.target.value) || 0 }))}
          />
        </label>
        <div className="modal-actions">
          {editId ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setEditId(null);
                setForm(EMPTY_EMOJI_FORM);
              }}
            >
              取消編輯
            </button>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {editId ? '更新' : '新增'}
          </button>
        </div>
      </form>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Emoji</th>
              <th>code</th>
              <th>名稱</th>
              <th>狀態</th>
              <th>排序</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>載入中…</td>
              </tr>
            ) : rows.length ? (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.emoji}</td>
                  <td>{row.code || '—'}</td>
                  <td>{row.name || '—'}</td>
                  <td>{row.status}</td>
                  <td>{row.sort ?? 0}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => {
                        setEditId(row.id);
                        setForm({
                          emoji: row.emoji,
                          code: row.code || '',
                          name: row.name || '',
                          keywords: row.keywords || '',
                          renderType: row.renderType || 'UNICODE',
                          status: row.status || 'ACTIVE',
                          sort: row.sort ?? 0,
                        });
                      }}
                    >
                      編輯
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>尚無表情</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
